import * as React from 'react'
import { useAuth } from '@/auth/AuthContext'
import { can } from '@/auth/access'
import { isInVenueSupportMode } from '@/auth/roles'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Image as ImageIcon, X } from 'lucide-react'
import { useFurniture, useCreateFurniture, useUpdateFurniture, useFurnitureUsage } from '@/hooks/useFurniture'
import { PlanImpactDialog } from '@/components/PlanImpactDialog'
import type { FurnitureUsageDto } from '@lib/dto/plan-impact.dto'
import { uploadService, UploadsUnavailableError } from '@/services/UploadService'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Facts } from '@/components/ui/facts'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { FormActions } from '@/components/ui/form-actions'
import { FormError } from '@/components/ui/form-error'
import { fileToThumbnail } from '@/helpers/imageThumbnail'
import { centsToInput, dollarsToCents, formatPrice } from '@/lib/price'
import { FurnitureEditor } from '@/components/lazyDrawings'
import { FurnitureTile } from '@/components/FurnitureTile'
import { DanceFloorFields } from '@/components/DanceFloorFields'
import { RiserFields } from '@/components/RiserFields'
import { createFurnitureItemInputSchema, updateFurnitureItemInputSchema } from '@lib/input/furniture-item.input'
import { FURNITURE_CLASSES, furnitureClassLabel, isDrawnClass, type FurnitureClass } from '@lib/enums/furniture-class.enum'
import { danceFloorFormValues } from '@lib/helpers/dance-floor'
import { riserFormValues } from '@lib/helpers/riser'
import { SUGGESTED_FURNITURE_TAGS } from '@lib/helpers/furniture-tag'
import { FURNITURE_MAX_CAPACITY_MAX } from '@lib/helpers/furniture-capacity'
import { TagField } from '@/components/TagField'
import { useFurnitureTags } from '@/hooks/useFurnitureTags'
import { useFurnitureGroups } from '@/hooks/useFurnitureGroups'
import { arrangementsBrokenBy } from '@/lib/groupSeating'
import { capacityBreaksArrangementsMessage } from '@lib/helpers/furniture-capacity'
import type { Primitive } from '@lib/dto/primitive.dto'
import type { FurnitureItemDto } from '@lib/dto/furniture-item.dto'

/**
 * Seats typed for a table. Blank is null: nobody has said, and a save stores
 * that as no count. A positive whole number is that count. Anything else is
 * undefined. Null is also how a save clears seats, so "8.5" or "0" must not
 * become null and navigate away as a successful clear.
 */
export function tableSeatCount(value: string): number | null | undefined {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (Number.isInteger(n) && n > 0) return n
  return undefined
}

/** Narrow a select's string value to a FurnitureClass without an unsafe cast. */
function furnitureClassValue(value: string): FurnitureClass {
  return FURNITURE_CLASSES.find((c) => c === value) ?? 'Other'
}

/**
 * What to offer under the tag box: what this venue already uses, then the
 * starting suggestions it has not adopted. Existing tags lead, because reusing
 * one is the point — the list is what keeps free text from sprawling.
 */
function tagSuggestions(inUse: readonly string[]): string[] {
  const seen = new Set(inUse.map((t) => t.toLocaleLowerCase()))
  return [...inUse, ...SUGGESTED_FURNITURE_TAGS.filter((t) => !seen.has(t.toLocaleLowerCase()))]
}

/**
 * Dedicated create/edit page for a catalog furniture item: its metadata, its
 * venue availability, and the shape designer. Replaces the old in-list dialog so
 * the shape editor has room to breathe.
 */
/**
 * Whether a save changes what the piece seats on plans still being arranged.
 * Seats are read from the piece wherever it is placed, so a new category or seat
 * count changes every one of those plans' totals at once.
 */
function seatsChangeReachesPlans(
  existing: FurnitureItemDto,
  nextClass: FurnitureClass,
  nextCapacity: number | null,
  usage: FurnitureUsageDto | undefined,
): boolean {
  if (!usage || !usage.plans.some((p) => !p.settled)) return false
  const seatsBefore = existing.class === 'Table' ? existing.maxCapacity : null
  const seatsAfter = nextClass === 'Table' ? nextCapacity : null
  return existing.class !== nextClass || seatsBefore !== seatsAfter
}

/**
 * Where the piece is in use, said above the form. Its shape is copied onto a
 * plan when it is placed, so a new shape reaches new placements only — worth
 * saying before somebody redraws a table expecting every plan to follow.
 */
function UsageNote({ usage }: { usage: FurnitureUsageDto }) {
  const events = new Set(usage.plans.map((p) => p.eventId)).size
  const open = usage.plans.filter((p) => !p.settled).length
  if (usage.plans.length === 0 && usage.layouts === 0 && usage.arrangements === 0) return null
  const parts = [
    usage.plans.length > 0
      ? `placed on ${String(usage.plans.length)} ${usage.plans.length === 1 ? 'plan' : 'plans'} across ${String(events)} ${events === 1 ? 'event' : 'events'} (${String(open)} still being arranged)`
      : null,
    usage.layouts > 0 ? `in ${String(usage.layouts)} saved ${usage.layouts === 1 ? 'layout' : 'layouts'}` : null,
    usage.arrangements > 0
      ? `in ${String(usage.arrangements)} ${usage.arrangements === 1 ? 'arrangement' : 'arrangements'}`
      : null,
  ].filter((p): p is string => p !== null)
  return (
    <p className="rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
      This piece is {parts.join(', ')}. Its name and seats show everywhere it is used; a new shape is used for new
      placements, and pieces already placed keep the shape they were placed with.
    </p>
  )
}

/** What the page asks for: a drawing, or the measurements a piece that is not drawn is made of. */
function pageDescription(furnitureClass: FurnitureClass): string {
  if (furnitureClass === 'DanceFloor') {
    return 'Name it, choose where it’s available, then set the size it starts at and the step it can grow by.'
  }
  if (furnitureClass === 'Riser') {
    return 'Name it, choose where it’s available, then set how wide and how long one unit is.'
  }
  return 'Name it, choose where it’s available, then draw its shape.'
}

/** A dance floor's or riser's measurements, said the way their fields ask for them; null for a drawn piece. */
function measurementsLabel(item: FurnitureItemDto): string | null {
  if (item.class === 'DanceFloor' && item.danceFloorDefaultFeet !== null) {
    const step = item.danceFloorIncrementFeet === null ? '' : `, grows by ${String(item.danceFloorIncrementFeet)} ft`
    return `Starts at ${String(item.danceFloorDefaultFeet)} ft${step}`
  }
  if (item.class === 'Riser' && item.riserUnitWidthFeet !== null && item.riserUnitLengthFeet !== null) {
    return `${String(item.riserUnitWidthFeet)} × ${String(item.riserUnitLengthFeet)} ft per unit`
  }
  return null
}

/**
 * A piece as someone reads it who can see the catalog but not change it: what
 * it is, what it costs and seats, and its likeness, with nothing to edit.
 */
function FurnitureItemFacts({ item, onBack }: { item: FurnitureItemDto; onBack: () => void }) {
  return (
    <PageContainer width="wide" className="space-y-6">
      <div className="space-y-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted transition-colors hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Furniture
        </button>
        <PageHeader title={item.name} description={furnitureClassLabel(item.class)} />
      </div>
      <div className="flex flex-wrap items-start gap-6">
        <FurnitureTile primitives={item.primitives} imageUrl={item.imageUrl} size={160} />
        <Facts
          layout="stack"
          className="min-w-0 flex-1"
          facts={[
            { label: 'Price / unit', value: item.price === null ? null : formatPrice(item.price), empty: 'Not set' },
            {
              label: 'Seats',
              value: item.class === 'Table' && item.maxCapacity !== null ? String(item.maxCapacity) : null,
              empty: item.class === 'Table' ? 'Not measured' : 'Only tables seat',
            },
            { label: 'Size', value: measurementsLabel(item), empty: 'As drawn' },
            { label: 'Description', value: item.description, empty: 'None' },
            {
              label: 'Tags',
              value:
                item.tags.length === 0 ? null : (
                  <span className="flex flex-wrap gap-1">
                    {item.tags.map((t) => (
                      <Badge key={t}>{t}</Badge>
                    ))}
                  </span>
                ),
              empty: 'Not set',
            },
          ]}
        />
      </div>
    </PageContainer>
  )
}

export function FurnitureItemPage() {
  const { itemId } = useParams<{ itemId: string }>()
  const isEdit = itemId !== undefined
  const navigate = useNavigate()

  const furnitureQuery = useFurniture()
  const { user } = useAuth()
  /** Without Edit on furniture, a piece is read here and changed by someone who can. */
  const canEdit = can(user, 'furniture:edit')
  /**
   * The venue this piece belongs to. It is not a choice on the form: a piece
   * belongs to one venue, and which one is already answered by the list this
   * editor was opened from, which says so in `?venue=`. An organization admin
   * can open a venue's settings without entering the venue, so the session's
   * own focus is only the fallback — relying on it alone left them unable to
   * add a piece to the very venue whose list they were standing in.
   */
  const [searchParams] = useSearchParams()
  const venueId = searchParams.get('venue') ?? (isInVenueSupportMode(user) ? user.supportVenue.venueId : null)
  /** Where "back" goes: the list this editor was opened from, a section of the
   * venue's settings. Without a venue there is no list, and `/furniture` sends
   * the session home. */
  const listPath = venueId === null ? '/furniture' : `/venues/${venueId}/settings#furniture`
  const createFurniture = useCreateFurniture()
  const updateFurniture = useUpdateFurniture()

  const existing: FurnitureItemDto | undefined = isEdit
    ? furnitureQuery.data?.items.find((i) => i.id === itemId)
    : undefined

  const [name, setName] = React.useState('')
  const [furnitureClass, setFurnitureClass] = React.useState<FurnitureClass>('Other')
  const [price, setPrice] = React.useState('')
  const [maxCapacity, setMaxCapacity] = React.useState('')
  const [danceFloorDefault, setDanceFloorDefault] = React.useState('')
  const [danceFloorIncrement, setDanceFloorIncrement] = React.useState('')
  const [riserWidth, setRiserWidth] = React.useState('')
  const [riserLength, setRiserLength] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [tags, setTags] = React.useState<string[]>([])
  const tagsQuery = useFurnitureTags(venueId)
  const groupsQuery = useFurnitureGroups(venueId)
  const [primitives, setPrimitives] = React.useState<Primitive[]>([])
  const [imageUrl, setImageUrl] = React.useState<string | null>(null)
  /** Signed/local preview when `imageUrl` is a private storage key after upload. */
  const [imagePreview, setImagePreview] = React.useState<string | null>(null)
  const [dragging, setDragging] = React.useState(false)
  const [submitError, setSubmitError] = React.useState<string | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  /** Bumped when a newer photo is picked or the current one is cleared, so a slow upload cannot land on the next piece. */
  const imageUpload = React.useRef(0)
  const [imageUploading, setImageUploading] = React.useState(false)
  const displayImage = imagePreview ?? imageUrl

  // Seed the form from the loaded item once (edit mode). Keyed on the row identity
  // so a background refetch doesn't clobber in-progress edits.
  const seededFrom = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (!existing || seededFrom.current === existing.id) return
    seededFrom.current = existing.id
    imageUpload.current += 1
    setImageUploading(false)
    setName(existing.name)
    setFurnitureClass(existing.class)
    setPrice(centsToInput(existing.price))
    setMaxCapacity(existing.maxCapacity === null ? '' : String(existing.maxCapacity))
    setDanceFloorDefault(existing.danceFloorDefaultFeet === null ? '' : String(existing.danceFloorDefaultFeet))
    setDanceFloorIncrement(existing.danceFloorIncrementFeet === null ? '' : String(existing.danceFloorIncrementFeet))
    setRiserWidth(existing.riserUnitWidthFeet === null ? '' : String(existing.riserUnitWidthFeet))
    setRiserLength(existing.riserUnitLengthFeet === null ? '' : String(existing.riserUnitLengthFeet))
    setDescription(existing.description ?? '')
    setTags(existing.tags)
    setPrimitives(existing.primitives)
    setImageUrl(existing.imageUrl)
    setImagePreview(null)
  }, [existing])

  /**
   * The arrangements this seat count would break, if it were saved.
   *
   * Only for a piece that already exists: a new one is in no arrangement yet.
   * The API refuses the same edit, so this is the same refusal arriving early
   * enough to be useful — with the arrangements named, since "some arrangement"
   * is not something anyone can go and fix.
   */
  const brokenArrangements = React.useMemo(() => {
    if (!isEdit || itemId === undefined || furnitureClass !== 'Table') return []
    const seats = tableSeatCount(maxCapacity)
    if (seats === undefined) return []
    return arrangementsBrokenBy(groupsQuery.data?.groups ?? [], itemId, seats)
  }, [isEdit, itemId, furnitureClass, maxCapacity, groupsQuery.data])

  const busy = createFurniture.isPending || updateFurniture.isPending

  function cancelImageUpload(): void {
    imageUpload.current += 1
    setImageUploading(false)
  }
  const danceFloor = danceFloorFormValues(furnitureClass, danceFloorDefault, danceFloorIncrement)
  const danceFloorError = danceFloor.error
  const riser = riserFormValues(furnitureClass, riserWidth, riserLength)
  const measureError = danceFloorError ?? riser.error
  const usageQuery = useFurnitureUsage(isEdit ? itemId : undefined)
  /** A save waiting on the seats confirmation — see `seatsChangeReachesPlans`. */
  const [pendingSave, setPendingSave] = React.useState<(() => Promise<void>) | null>(null)

  async function loadImageFile(file: File): Promise<void> {
    if (!file.type.startsWith('image/')) {
      setSubmitError('That file is not an image.')
      return
    }
    const generation = imageUpload.current + 1
    imageUpload.current = generation
    setImageUploading(true)
    try {
      const dataUrl = await fileToThumbnail(file)
      if (imageUpload.current !== generation) return
      // Prefer private object storage (persist the key; show signed URL for preview).
      // Fall back to the inline data URL if uploads aren't configured.
      try {
        const uploaded = await uploadService.uploadImage(dataUrl)
        if (imageUpload.current !== generation) return
        setImageUrl(uploaded.key)
        setImagePreview(uploaded.url)
      } catch (uploadError) {
        if (imageUpload.current !== generation) return
        if (!(uploadError instanceof UploadsUnavailableError)) throw uploadError
        setImageUrl(dataUrl)
        setImagePreview(null)
      }
      setSubmitError(null)
    } catch (error) {
      if (imageUpload.current === generation) setSubmitError(requestErrorMessage(error))
    } finally {
      if (imageUpload.current === generation) setImageUploading(false)
    }
  }

  async function handleFile(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0]
    event.target.value = '' // allow re-selecting the same file
    if (file) await loadImageFile(file)
  }

  async function handleImageDrop(event: React.DragEvent<HTMLDivElement>): Promise<void> {
    event.preventDefault()
    setDragging(false)
    const file = event.dataTransfer.files?.[0]
    if (file) await loadImageFile(file)
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setSubmitError(null)
    if (imageUploading) return
    const priceValue = dollarsToCents(price)
    if (priceValue === undefined) {
      setSubmitError('Enter the price in dollars, or leave it blank.')
      return
    }
    // Only a table seats anyone, and the API stores it that way too — sending
    // the number from a field that is no longer on screen would have the form
    // quietly disagree with what it shows.
    const capacityParsed = furnitureClass === 'Table' ? tableSeatCount(maxCapacity) : null
    if (capacityParsed === undefined) {
      setSubmitError('Enter a whole number of seats, or leave it blank.')
      return
    }
    const capacityValue = capacityParsed
    const descriptionValue = description.trim() === '' ? null : description.trim()
    // An empty list is "nobody has said", which is a real answer and not a blank.
    const tagsValue = tags
    // A new category or seat count reaches every plan the piece is on — their
    // seat totals are read from the piece, not stored with them. Asked first,
    // with the plans named, when any still being planned have it.
    if (
      isEdit &&
      existing &&
      pendingSave === null &&
      seatsChangeReachesPlans(existing, furnitureClass, capacityValue, usageQuery.data)
    ) {
      setPendingSave(() => save)
      return
    }
    await save()

    async function save(): Promise<void> {
      try {
        if (isEdit && itemId) {
          const parsed = updateFurnitureItemInputSchema.safeParse({
            name,
            class: furnitureClass,
            primitives,
            imageUrl,
            price: priceValue,
            maxCapacity: capacityValue,
            danceFloorDefaultFeet: danceFloor.defaultFeet,
            danceFloorIncrementFeet: danceFloor.incrementFeet,
            riserUnitWidthFeet: riser.widthFeet,
            riserUnitLengthFeet: riser.lengthFeet,
            description: descriptionValue,
            tags: tagsValue,
          })
          if (!parsed.success) {
            setSubmitError(parsed.error.issues[0]?.message ?? 'Invalid input')
            return
          }
          await updateFurniture.mutateAsync({ id: itemId, input: parsed.data })
        } else {
          if (venueId === null) {
            setSubmitError('Enter a venue before adding furniture — a piece belongs to one.')
            return
          }
          const parsed = createFurnitureItemInputSchema.safeParse({
            name,
            class: furnitureClass,
            primitives,
            venueId,
            price: priceValue,
            maxCapacity: capacityValue,
            danceFloorDefaultFeet: danceFloor.defaultFeet,
            danceFloorIncrementFeet: danceFloor.incrementFeet,
            riserUnitWidthFeet: riser.widthFeet,
            riserUnitLengthFeet: riser.lengthFeet,
            description: descriptionValue,
            ...(tagsValue.length === 0 ? {} : { tags: tagsValue }),
            ...(imageUrl ? { imageUrl } : {}),
          })
          if (!parsed.success) {
            setSubmitError(parsed.error.issues[0]?.message ?? 'Invalid input')
            return
          }
          await createFurniture.mutateAsync(parsed.data)
        }
        navigate(listPath)
      } catch (error) {
        setSubmitError(requestErrorMessage(error))
      }
    }
  }

  if (isEdit && furnitureQuery.isPending) return <LoadingState label="Loading furniture…" />
  if (isEdit && furnitureQuery.isError) {
    return <ErrorState message={requestErrorMessage(furnitureQuery.error)} onRetry={() => void furnitureQuery.refetch()} />
  }
  if (isEdit && !existing) {
    return <ErrorState message="That furniture item no longer exists." onRetry={() => navigate(listPath)} />
  }
  if (existing && !canEdit) return <FurnitureItemFacts item={existing} onBack={() => navigate(listPath)} />

  return (
    <PageContainer width="wide" className="space-y-6">
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => navigate(listPath)}
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted transition-colors hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Furniture
        </button>
        <PageHeader
          title={isEdit ? 'Edit furniture item' : 'New furniture item'}
          description={pageDescription(furnitureClass)}
        />
      </div>

      {isEdit && usageQuery.data ? <UsageNote usage={usageQuery.data} /> : null}
      <PlanImpactDialog
        open={pendingSave !== null}
        onOpenChange={(open) => {
          if (!open) setPendingSave(null)
        }}
        title="Change seating on plans that use this piece?"
        intro={(n) =>
          `${String(n)} ${n === 1 ? 'plan still being arranged has' : 'plans still being arranged have'} this piece. Their seat totals will change with it.`
        }
        piecesLabel={(p) => `${String(p)} placed`}
        settledNote={(c) =>
          `${String(c)} confirmed or finished ${c === 1 ? 'plan' : 'plans'} also ${c === 1 ? 'has' : 'have'} it; seat counts there are read from the piece too.`
        }
        impact={
          usageQuery.data
            ? {
                plans: usageQuery.data.plans.filter((p) => !p.settled),
                settledKept: usageQuery.data.plans.filter((p) => p.settled).length,
                blocking: [],
              }
            : null
        }
        loading={false}
        error={null}
        confirmLabel="Save"
        busy={busy}
        onConfirm={() => {
          const run = pendingSave
          setPendingSave(null)
          if (run) void run()
        }}
      />

      <form className="space-y-6" onSubmit={handleSubmit} noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" htmlFor="fi-name">
            <Input id="fi-name" value={name} onChange={(e) => setName(e.target.value)} placeholder='Round Table (60")' autoFocus />
          </Field>
          <Field label="Class" htmlFor="fi-class">
            <Select id="fi-class" value={furnitureClass} onChange={(e) => setFurnitureClass(furnitureClassValue(e.target.value))}>
              {FURNITURE_CLASSES.map((c) => (
                <option key={c} value={c}>
                  {furnitureClassLabel(c)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Price / unit ($)" htmlFor="fi-price">
            <Input id="fi-price" type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="e.g. 12.50" />
          </Field>
          {/* Only a table seats anyone, so the field appears only for one. Left
              blank it stays blank: a plan's seat total counts the tables that
              carry a number and says how many do not, rather than treating an
              unanswered table as seating nobody. */}
          {furnitureClass === 'DanceFloor' && (
            <DanceFloorFields
              idPrefix="fi"
              defaultFeet={danceFloorDefault}
              incrementFeet={danceFloorIncrement}
              onDefaultFeet={setDanceFloorDefault}
              onIncrementFeet={setDanceFloorIncrement}
              error={danceFloorError}
            />
          )}
          {furnitureClass === 'Riser' && (
            <RiserFields
              idPrefix="fi"
              widthFeet={riserWidth}
              lengthFeet={riserLength}
              onWidthFeet={setRiserWidth}
              onLengthFeet={setRiserLength}
              error={riser.error}
            />
          )}
          {furnitureClass === 'Table' && (
            <Field
              label="Max capacity (seats)"
              htmlFor="fi-capacity"
              hint="The most chairs that fit at this table. Leave blank if you have not measured it."
              {...(brokenArrangements.length > 0
                ? { error: capacityBreaksArrangementsMessage(name, tableSeatCount(maxCapacity) ?? 0, brokenArrangements) }
                : {})}
            >
              <Input
                id="fi-capacity"
                type="number"
                min={1}
                max={FURNITURE_MAX_CAPACITY_MAX}
                step="1"
                value={maxCapacity}
                onChange={(e) => setMaxCapacity(e.target.value)}
                placeholder="e.g. 8"
              />
            </Field>
          )}
          <Field label="Description" htmlFor="fi-desc">
            <Input id="fi-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Material, color, etc. (optional)" />
          </Field>
          <Field label="Tags" htmlFor="fi-tag">
            {/* Free text with suggestions, not a picker: a venue may sort its
                furniture by anything, and the list is a prompt rather than the
                set of allowed answers. */}
            <TagField
              id="fi-tag"
              value={tags}
              suggestions={tagSuggestions(tagsQuery.data?.tags ?? [])}
              onChange={setTags}
              placeholder="e.g. Vendor-Rental (optional)"
            />
          </Field>
        </div>

        <Field label="Thumbnail">
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                fileInputRef.current?.click()
              }
            }}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleImageDrop}
            className={`flex cursor-pointer items-center gap-3 rounded-md border border-dashed px-3 py-3 transition-colors ${
              dragging ? 'border-primary bg-accent-soft' : 'border-line hover:bg-surface-hover'
            }`}
          >
            {displayImage ? (
              <img src={displayImage} alt="" className="h-14 w-14 shrink-0 rounded-md border border-line object-cover" />
            ) : (
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-surface-sunken text-ink-muted">
                <ImageIcon className="h-5 w-5" />
              </div>
            )}
            <div className="min-w-0 flex-1 text-sm">
              <p className="font-medium text-ink">{imageUrl ? 'Replace photo' : 'Add a photo'}</p>
              <p className="text-ink-muted">Drag an image here, or click to browse — optional.</p>
            </div>
            {imageUrl ? (
              <button
                type="button"
                aria-label="Remove photo"
                onClick={(e) => {
                  e.stopPropagation()
                  cancelImageUpload()
                  setImageUrl(null)
                  setImagePreview(null)
                }}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface hover:text-ink"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          </div>
        </Field>

        {isDrawnClass(furnitureClass) && (
          <Field label="Shape">
            <FurnitureEditor primitives={primitives} onChange={setPrimitives} />
          </Field>
        )}

        {submitError ? <FormError>{submitError}</FormError> : null}

        <FormActions>
          <Button type="button" variant="outline" onClick={() => navigate(listPath)}>
            Cancel
          </Button>
          {/* The API refuses this edit, so the button that would send it is shut.
              The reason is already beside the field that caused it. */}
          <Button pending={busy || imageUploading} type="submit" disabled={brokenArrangements.length > 0 || measureError !== null || imageUploading}>
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Add item'}
          </Button>
        </FormActions>
      </form>
    </PageContainer>
  )
}
